import React from 'react';
import { Card, Col, Row, Statistic } from 'antd';
import type { HostDashboard, HostTenantStatus } from '../../tenants/api/tenants';
import type { LicenseFilter } from '../utils/tenantStatus';

interface HostTenantStatsProps {
  dashboard: HostDashboard | undefined;
  loading: boolean;
  activeStatus?: HostTenantStatus;
  activeLicenseFilter: LicenseFilter;
  onStatusClick: (status?: HostTenantStatus) => void;
  onExpiringClick: () => void;
}

interface StatCardProps {
  title: string;
  value: number;
  loading: boolean;
  active: boolean;
  onClick: () => void;
}

function StatCard({ title, value, loading, active, onClick }: StatCardProps) {
  return (
    <Card
      bordered={false}
      loading={loading}
      hoverable
      onClick={onClick}
      style={{
        cursor: 'pointer',
        outline: active ? '2px solid var(--ant-color-primary)' : undefined,
      }}
    >
      <Statistic title={title} value={value} />
    </Card>
  );
}

const HostTenantStats: React.FC<HostTenantStatsProps> = ({
  dashboard,
  loading,
  activeStatus,
  activeLicenseFilter,
  onStatusClick,
  onExpiringClick,
}) => {
  return (
    <Row gutter={[16, 16]} style={{ marginBottom: 8 }}>
      <Col xs={24} sm={12} lg={4}>
        <StatCard
          title="Total tenants"
          value={dashboard?.totalCount ?? 0}
          loading={loading}
          active={activeStatus === undefined && activeLicenseFilter === 'all'}
          onClick={() => onStatusClick(undefined)}
        />
      </Col>
      <Col xs={24} sm={12} lg={4}>
        <StatCard
          title="Requested"
          value={dashboard?.requestedCount ?? 0}
          loading={loading}
          active={activeStatus === 'Requested'}
          onClick={() => onStatusClick('Requested')}
        />
      </Col>
      <Col xs={24} sm={12} lg={4}>
        <StatCard
          title="Approved"
          value={dashboard?.approvedCount ?? 0}
          loading={loading}
          active={activeStatus === 'Approved'}
          onClick={() => onStatusClick('Approved')}
        />
      </Col>
      <Col xs={24} sm={12} lg={4}>
        <StatCard
          title="Active"
          value={dashboard?.activeCount ?? 0}
          loading={loading}
          active={activeStatus === 'Active' && activeLicenseFilter === 'all'}
          onClick={() => onStatusClick('Active')}
        />
      </Col>
      <Col xs={24} sm={12} lg={4}>
        <StatCard
          title="Expired"
          value={dashboard?.expiredCount ?? 0}
          loading={loading}
          active={activeStatus === 'Expired'}
          onClick={() => onStatusClick('Expired')}
        />
      </Col>
      <Col xs={24} sm={12} lg={4}>
        <StatCard
          title="Rejected"
          value={dashboard?.rejectedCount ?? 0}
          loading={loading}
          active={activeStatus === 'Rejected'}
          onClick={() => onStatusClick('Rejected')}
        />
      </Col>
      <Col xs={24} sm={12} lg={4}>
        <StatCard
          title="Expiring soon"
          value={dashboard?.expiringWithinWarningDaysCount ?? 0}
          loading={loading}
          active={activeLicenseFilter === 'expiring'}
          onClick={onExpiringClick}
        />
      </Col>
    </Row>
  );
};

export default HostTenantStats;
